import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMemberComplaints } from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_MY_COMPLAINTS_VIEW,
    SOCIETY_PORTAL_MY_COMPLAINTS_RAISE,
} from '../../../Routing/routes';
import { formatIstDate } from '../../../utils/helpers/istDate';

const STATUS_STYLE = {
    PENDING:     'tw-bg-blue-100 tw-text-blue-700',
    IN_PROGRESS: 'tw-bg-amber-100 tw-text-amber-700',
    COMPLETED:   'tw-bg-emerald-100 tw-text-emerald-700',
    REOPENED:    'tw-bg-rose-100 tw-text-rose-700',
};

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (d) => d ? formatIstDate(d) : '—';

const MyComplaints = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    // /api/member/complaints resolves "mine" from the token — complaints the
    // member raised, or logged against a flat they hold — so nothing about
    // identity is sent here and only member_portal.complaints is needed.
    const {
        complaintsData: complaintsListData,
        complaintsLoading: complaintsListLoading,
    } = useSelector(state => state.MemberPortalReducer || {});

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (!societyId) return;
        dispatch(getMemberComplaints({ societyId, page: 1, limit: 50 }));
    }, [dispatch, societyId, refreshKey]);

    const rows = useMemo(
        () => Array.isArray(complaintsListData) ? complaintsListData : [],
        [complaintsListData]
    );
    const counts = useMemo(() => rows.reduce((acc, c) => {
        acc[c.status] = (acc[c.status] || 0) + 1;
        return acc;
    }, {}), [rows]);

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <div className="tw-max-w-5xl tw-mx-auto tw-space-y-4">
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">My Complaints</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Everything you've raised in this society</p>
                    </div>
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS_RAISE)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-shadow-sm"
                    >
                        <Icon icon="mdi:plus" className="tw-text-base" />
                        Raise Complaint
                    </button>
                </div>

                <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-3">
                    {['PENDING', 'IN_PROGRESS', 'COMPLETED', 'REOPENED'].map(s => (
                        <div key={s} className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-3">
                            <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">{humanize(s)}</p>
                            <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-tight tw-mt-1">{counts[s] || 0}</p>
                        </div>
                    ))}
                </div>

                {complaintsListLoading ? (
                    <div className="tw-flex tw-justify-center tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-p-10 tw-text-center">
                        <div className="tw-mx-auto tw-w-12 tw-h-12 tw-rounded-full tw-bg-rose-50 tw-text-rose-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:message-outline" className="tw-text-2xl" />
                        </div>
                        <h3 className="tw-mt-3 tw-text-sm tw-font-bold tw-text-gray-800">You haven't raised any complaints yet</h3>
                        <p className="tw-mt-1 tw-text-[11px] tw-text-gray-500">Report an issue with your flat, common areas, security, or amenities and track it here.</p>
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS_RAISE)}
                            className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700"
                        >
                            <Icon icon="mdi:plus" /> Raise Your First Complaint
                        </button>
                    </div>
                ) : (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-divide-y tw-divide-gray-50">
                        {rows.map(c => (
                            <button
                                key={c.complaint_id}
                                onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS_VIEW.replace(':id', c.complaint_id))}
                                className="tw-w-full tw-text-left tw-p-4 hover:tw-bg-slate-50/50 tw-transition-colors tw-flex tw-items-start tw-gap-3"
                            >
                                <div className="tw-mt-0.5 tw-w-9 tw-h-9 tw-rounded-lg tw-bg-slate-100 tw-text-slate-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                    <Icon icon={c.category?.icon || 'mdi:tag-outline'} className="tw-text-lg" />
                                </div>
                                <div className="tw-flex-1 tw-min-w-0">
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                        <span className="tw-text-[11px] tw-font-mono tw-font-semibold tw-text-gray-500">{c.complaint_code}</span>
                                        <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[c.status] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                            {humanize(c.status)}
                                        </span>
                                        {c.category?.name && (
                                            <span className="tw-text-[10px] tw-text-gray-500">{c.category.name}</span>
                                        )}
                                        {c.status === 'COMPLETED' && !c.rating && (
                                            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-text-amber-800 tw-bg-amber-100 tw-px-1.5 tw-py-0.5 tw-rounded-full">
                                                <Icon icon="mdi:star-outline" /> Rate resolution
                                            </span>
                                        )}
                                        {c.rating > 0 && (
                                            <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-text-[10px] tw-text-amber-600">
                                                {[1, 2, 3, 4, 5].map(n => (
                                                    <Icon key={n} icon={n <= c.rating ? 'mdi:star' : 'mdi:star-outline'} />
                                                ))}
                                            </span>
                                        )}
                                    </div>
                                    <p className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mt-1 tw-truncate">{c.subject}</p>
                                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5 tw-line-clamp-2">{c.description}</p>
                                    <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2 tw-text-[10px] tw-text-gray-500">
                                        <span>Raised {fmtDate(c.created_at)}</span>
                                        {c.due_date && <span>Due {fmtDate(c.due_date)}</span>}
                                        {c.assignedTo && <span>Assigned to {[c.assignedTo.first_name, c.assignedTo.last_name].filter(Boolean).join(' ')}</span>}
                                    </div>
                                </div>
                                <Icon icon="mdi:chevron-right" className="tw-text-gray-400 tw-text-lg tw-mt-2" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyComplaints;
